/**
 * Weekly theme proposals (#845, docs/plans/2026-10-02-conversation-themes.md §4).
 *
 * The closed state set of the Postgres table `org_theme_proposals` (migration
 * 202610091230_org_theme_proposals.sql). The same list is the table's status
 * CHECK, so a value added here without a migration is rejected by the
 * database. Below it (PR 5b): the routes `/api/v1/analytics/voice/themes/proposals`,
 * the model's answer of the weekly run and the params of its Workflow. The
 * limits and windows are `@aica/core/voc/theme-proposals` — re-exported here.
 *
 * - `proposed`: shown to the editors of the set
 * - `accepting`: «Accept» pressed — the code is reserved in D1, the theme is
 *   not active yet (reconciliation finishes or rolls it back)
 * - `accepted`: the theme is active in the set
 * - `rejected`: the key stays silent for 90 days
 * - `expired`: below the threshold in the current window; hidden, comes back
 *   when it passes again
 */
import { z } from 'zod/v4';
import {
  THEME_PROPOSALS_MAX_CANDIDATES,
  THEME_PROPOSALS_MAX_PHRASES,
  THEME_PROPOSALS_WINDOW_DAYS,
  THEME_PROPOSAL_EXAMPLES,
  THEME_PROPOSAL_MIN_CALLS,
  THEME_PROPOSAL_MIN_SHARE,
  THEME_PROPOSAL_SILENCE_DAYS,
} from '@aica/core/voc/theme-proposals';
import { orgThemeSchema } from './org-themes';

export const ORG_THEME_PROPOSAL_STATUSES = [
  'proposed',
  'accepting',
  'accepted',
  'rejected',
  'expired',
] as const;
export const orgThemeProposalStatusSchema = z.enum(ORG_THEME_PROPOSAL_STATUSES);
export type OrgThemeProposalStatus = z.infer<typeof orgThemeProposalStatusSchema>;

// ============================================================================
// PR 5b — the weekly run, the routes, the model's answer
// ============================================================================

export {
  THEME_PROPOSALS_MAX_CANDIDATES,
  THEME_PROPOSALS_MAX_PHRASES,
  THEME_PROPOSALS_WINDOW_DAYS,
  THEME_PROPOSAL_EXAMPLES,
  THEME_PROPOSAL_MIN_CALLS,
  THEME_PROPOSAL_MIN_SHARE,
  THEME_PROPOSAL_SILENCE_DAYS,
};

/** The statuses the editors' list shows: pending, and «Accept» pressed but not finished. */
export const SHOWN_ORG_THEME_PROPOSAL_STATUSES = [
  'proposed',
  'accepting',
] as const satisfies readonly OrgThemeProposalStatus[];

/** `:id` of a proposal route — `org_theme_proposals.id`, a UUID. */
export const orgThemeProposalIdParamSchema = z.object({ id: z.uuid() });

/**
 * An example conversation of a proposal: a record of the caller's organization
 * inside the section's project scope (the reader joins `records` and drops the
 * rest), linked from the screen.
 */
export const orgThemeProposalExampleSchema = z.object({
  recordId: z.string().min(1),
  createdAt: z.string().min(1),
});

/**
 * A proposal as the editors see it. `calls` / `share` were counted by SQL over
 * the stored phrase keys in the window `[windowStart, windowEnd)` — not by the
 * model. `needsDefinition` — the model's definition did not survive
 * minimization: the theme is accepted with an empty one, to be refined.
 */
export const orgThemeProposalSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  definition: z.string(),
  needsDefinition: z.boolean(),
  calls: z.number().int().min(0),
  share: z.number().min(0).max(1),
  status: z.enum(SHOWN_ORG_THEME_PROPOSAL_STATUSES),
  windowStart: z.string().min(1),
  windowEnd: z.string().min(1),
  examples: z.array(orgThemeProposalExampleSchema).max(THEME_PROPOSAL_EXAMPLES),
});

export const listOrgThemeProposalsResponseSchema = z.object({
  proposals: z.array(orgThemeProposalSchema),
});

/** «Accept»: the theme as the set shows it (active now). */
export const acceptOrgThemeProposalResponseSchema = z.object({
  proposalId: z.string().min(1),
  theme: orgThemeSchema,
});

/** «Reject»: the key is silent for 90 days. */
export const rejectOrgThemeProposalResponseSchema = z.object({
  proposalId: z.string().min(1),
  status: z.literal('rejected'),
});

/**
 * Refusal codes of the proposal routes (body `{ error, code }`):
 * - `proposal_not_found` 404 — no such proposal in the organization (or not
 *   shown any more: expired, accepted, rejected, deleted);
 * - `proposal_status_conflict` 409 — the proposal is not in a state the action
 *   allows (reject of one being accepted);
 * - `proposal_changed` 409 — the theme reserved for it vanished meanwhile; the
 *   proposal is back to `proposed`, press again;
 * - `theme_set_full` 409 — 30 themes in `active` + `reserved`: archive one; the
 *   proposal stays `proposed`;
 * - `tier_not_eligible` / `feature_disabled` / `themes_disabled` 403 — the
 *   gates of the theme set (the same order as its own routes);
 * - `proposal_invalid` 400 — the `:id` is not a proposal id.
 */
export const ORG_THEME_PROPOSAL_ERROR_CODES = [
  'proposal_not_found',
  'proposal_status_conflict',
  'proposal_changed',
  'theme_set_full',
  'tier_not_eligible',
  'feature_disabled',
  'themes_disabled',
  'proposal_invalid',
] as const;

export type OrgThemeProposalErrorCode = (typeof ORG_THEME_PROPOSAL_ERROR_CODES)[number];

export const orgThemeProposalErrorResponseSchema = z.object({
  error: z.string(),
  code: z.enum(ORG_THEME_PROPOSAL_ERROR_CODES),
  cap: z.number().int().optional(),
});

export type OrgThemeProposalExample = z.infer<typeof orgThemeProposalExampleSchema>;
export type OrgThemeProposal = z.infer<typeof orgThemeProposalSchema>;
export type ListOrgThemeProposalsResponse = z.infer<
  typeof listOrgThemeProposalsResponseSchema
>;
export type AcceptOrgThemeProposalResponse = z.infer<
  typeof acceptOrgThemeProposalResponseSchema
>;
export type RejectOrgThemeProposalResponse = z.infer<
  typeof rejectOrgThemeProposalResponseSchema
>;
export type OrgThemeProposalErrorResponse = z.infer<
  typeof orgThemeProposalErrorResponseSchema
>;

// ============================================================================
// The model's answer (validated before anything of it is kept)
// ============================================================================

export const themeProposalsModelCandidateSchema = z.object({
  name: z.string(),
  definition: z.string(),
  // Any number: a stray non-integer or out-of-range number is dropped by the
  // parser, not the whole candidate.
  phrases: z.array(z.number()),
});

export const themeProposalsModelAnswerSchema = z.object({
  candidates: z.array(z.unknown()),
});

// ============================================================================
// ThemeProposalsWorkflow params (cron → Workflow)
// ============================================================================

/**
 * The params of `ThemeProposalsWorkflow`: the organization and the end of the
 * run's window (the cron's week). Deliberately no data of the conversations —
 * a Workflow's params and step outputs outlive the proposals' retention, so no
 * phrase may travel here.
 */
export const themeProposalsWorkflowInputSchema = z
  .object({
    orgId: z.string().min(1).max(255),
    /** End of the 30-day window, ISO 8601: the week the cron started the run for. */
    windowEnd: z.iso.datetime(),
    traceId: z.string().max(128).optional(),
  })
  .strict();

export type ThemeProposalsWorkflowInput = z.infer<
  typeof themeProposalsWorkflowInputSchema
>;
